import {
  getLabelDotDimensions,
  LABEL_LAYOUT,
  LABEL_MEDIA,
} from '../config_and_helpers/labelPrinterConfig';

const PLACEHOLDER = '—';
const EZPL_LINE_END = '\r\n';

/**
 * @param {string|null|undefined} value
 * @returns {string}
 */
export const sanitizeEzplText = value => {
  if (value == null || value === '') return PLACEHOLDER;
  return String(value)
    .replace(/[\r\n]+/g, ' ')
    .replace(/[\^~,]/g, ' ')
    .trim();
};

/**
 * @param {string|null|undefined} value
 * @param {number} maximumLength
 * @returns {string}
 */
export const truncateText = (value, maximumLength) => {
  const clean = sanitizeEzplText(value);
  if (clean === PLACEHOLDER) return clean;
  if (clean.length <= maximumLength) return clean;
  return `${clean.slice(0, Math.max(0, maximumLength - 1))}…`;
};

/**
 * @param {string|null|undefined} name
 * @returns {[string, string|null]}
 */
export const splitProductName = name => {
  const clean = sanitizeEzplText(name);
  if (clean === PLACEHOLDER) return [clean, null];
  const max = LABEL_LAYOUT.productMaxLen;
  if (clean.length <= max) return [clean, null];
  const splitAt = clean.lastIndexOf(' ', max);
  const breakAt = splitAt > 8 ? splitAt : max;
  const line1 = clean.slice(0, breakAt).trim();
  const line2 = truncateText(clean.slice(breakAt).trim(), max);
  return [line1, line2 === PLACEHOLDER ? null : line2];
};

/**
 * @param {string|null|undefined} value
 * @returns {string}
 */
export const formatExpiry = value => {
  if (!value) return PLACEHOLDER;
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  return sanitizeEzplText(str);
};

/**
 * UTF-8 byte length for EZPL QR len parameter.
 * @param {string} value
 * @returns {number}
 */
export const calculateEncodedByteLength = value => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  return unescape(encodeURIComponent(value)).length;
};

/**
 * @param {number} x
 * @param {number} y
 * @param {string} text
 * @returns {string}
 */
const adText = (x, y, text) => `AD,${x},${y},1,1,0,0,${text}${EZPL_LINE_END}`;

/**
 * Build one EZPL label block for a stock item.
 * @param {object} label
 * @returns {string}
 */
export const buildStockItemLabel = label => {
  const scanUrl = String(label.scan_url || '').trim();
  if (!scanUrl) {
    throw new Error('Label missing scan_url');
  }

  const { widthDots, heightDots } = getLabelDotDimensions();
  const [productLine1, productLine2] = splitProductName(label.product_name);
  const cat = truncateText(label.cat_num, 16);
  const batch = truncateText(label.batch, 12);
  const expiry = formatExpiry(label.expiry);
  const id = label.stock_item_id != null ? String(label.stock_item_id) : PLACEHOLDER;

  const metaLine = truncateText(
    `Cat:${cat} B:${batch} Exp:${expiry}`,
    LABEL_LAYOUT.metaMaxLen
  );
  const urlLen = calculateEncodedByteLength(scanUrl);

  const lines = [
    `^Q${widthDots},${heightDots}${EZPL_LINE_END}`,
    `^W${widthDots}${EZPL_LINE_END}`,
    `^H${LABEL_MEDIA.dpi}${EZPL_LINE_END}`,
    `^P1${EZPL_LINE_END}`,
    `^L${EZPL_LINE_END}`,
    `W${LABEL_LAYOUT.qrX},${LABEL_LAYOUT.qrY},2,2,M,8,${LABEL_LAYOUT.qrMul},${urlLen},0${EZPL_LINE_END}`,
    `${scanUrl}${EZPL_LINE_END}`,
    adText(LABEL_LAYOUT.textX, LABEL_LAYOUT.line1Y, productLine1),
  ];

  if (productLine2) {
    lines.push(adText(LABEL_LAYOUT.textX, LABEL_LAYOUT.line2Y, productLine2));
    lines.push(adText(LABEL_LAYOUT.textX, LABEL_LAYOUT.line3Y, metaLine));
    lines.push(adText(LABEL_LAYOUT.textX, 16, `ID:${id}`));
  } else {
    lines.push(adText(LABEL_LAYOUT.textX, LABEL_LAYOUT.line2Y, metaLine));
    lines.push(adText(LABEL_LAYOUT.textX, LABEL_LAYOUT.line3Y, `ID:${id}`));
  }

  lines.push('E');
  return lines.join('');
};

/**
 * @param {object[]} labels
 * @returns {string}
 */
export const buildOrderLabelBatch = labels => {
  if (!labels?.length) return '';
  return labels.map(buildStockItemLabel).join(EZPL_LINE_END);
};

/**
 * @param {number} orderId
 * @param {object[]} labels
 * @returns {string}
 */
export const buildPrintJobName = (orderId, labels) =>
  `Materiah order ${orderId} — ${labels?.length ?? 0} stock labels`;
